import {forwardRef} from 'react';

import {Icon} from '../Icon';
import {LoadingSpinner} from '../LoadingSpinner';
import {Text} from '../Text';

import {ButtonComponent, ButtonProps} from './Button.types';

import {buttonStyles, spinnerStyles} from './Button.css';

/**
 * Buttons allow users to take actions, and make choices, with a single tap.
 *
 * When a `ref` prop is provided, it will be forwarded to the root element. Any
 * other properties supplied will be provided to the root element (ie, the `as`
 * prop value). This includes all appropriate HTML attributes or aria tags. This
 * component may have it's underlying DOM customized via the 'as' prop.
 */
export const Button: ButtonComponent = forwardRef(
  <T extends React.ElementType = 'button'>(
    {
      icon,
      as,
      children,
      className,
      type,
      variant = 'primary',
      width,
      loading,
      ...otherProps
    }: ButtonProps<T>,
    ref?: PolymorphicRef<T>,
  ) => {
    const Component = as || 'button';
    const fallbackType = Component === 'button' && !type ? 'button' : undefined;
    const withIcon = !!icon;
    const withChildren = !!children;

    return (
      <Component
        className={`${buttonStyles({
          variant,
          iconOnly: withIcon && !withChildren,
        })} ${className}`}
        ref={ref}
        type={type || fallbackType}
        variant={variant}
        {...otherProps}>
        {loading ? (
          <div className={spinnerStyles}>
            <LoadingSpinner size="normal" color="currentColor" />
          </div>
        ) : (
          <>
            {withChildren && (
              <Text variant="button" color="inherit">
                {children}
              </Text>
            )}
            {withIcon && <Icon name={icon} size="small" color="currentColor" weight="bold" />}
          </>
        )}
      </Component>
    );
  },
);
